/**
 * PDF Preview Gallery — admin-only live preview for all premium PDF generators.
 *
 * UX:
 *  - Left sidebar: sample type picker (Invoice / RFQ / LOI / Document / GRN)
 *                  + issuer company switcher (all BMP companies)
 *  - Main: live <iframe> preview with current selection
 *  - Top-right: Download button
 *
 * Uses the `?token=...` query-auth pattern (same as Invoice/GRN preview) so the
 * iframe can render without needing an Authorization header.
 */

import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  FileText, ShoppingCart, Send, FileSignature, Package,
  Download, Building2, Loader2, Sparkles, RefreshCcw,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_TYPES = [
  {
    code: 'invoice',
    label: 'Invoice',
    icon: FileText,
    color: '#023E8A',
    description: 'Tax Invoice with line items, VAT, bank details, and BILL TO block.',
  },
  {
    code: 'rfq',
    label: 'RFQ',
    icon: ShoppingCart,
    color: '#059669',
    description: 'Request for Quotation — supplier-facing, no prices shown by default.',
  },
  {
    code: 'loi',
    label: 'LOI',
    icon: Send,
    color: '#D97706',
    description: 'Letter of Intent — institutional letter format for supply commitments.',
  },
  {
    code: 'document',
    label: 'Document (NCNDA)',
    icon: FileSignature,
    color: '#8B5CF6',
    description: 'Sample NCNDA rendered from the Document Templates engine.',
  },
  {
    code: 'grn',
    label: 'GRN',
    icon: Package,
    color: '#DC2626',
    description: 'Goods Received Note with partial delivery and inspection notes.',
  },
];

const PDFPreviewGallery = () => {
  const [activeType, setActiveType] = useState('invoice');
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const token = useMemo(() => localStorage.getItem('admin_token') || '', []);

  useEffect(() => {
    (async () => {
      try {
        setLoadingCompanies(true);
        const r = await axios.get(
          `${API_URL}/api/cms/pdf-preview-gallery/meta/available-companies`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCompanies(r.data || []);
        if (r.data?.length && !activeCompanyId) {
          setActiveCompanyId(r.data[0].id);
        }
      } catch (_) {
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeMeta = useMemo(
    () => SAMPLE_TYPES.find((t) => t.code === activeType) || SAMPLE_TYPES[0],
    [activeType],
  );
  const activeCompany = useMemo(
    () => companies.find((c) => c.id === activeCompanyId),
    [companies, activeCompanyId],
  );

  const previewUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (activeCompanyId) params.set('company_id', activeCompanyId);
    params.set('_r', String(refreshKey));
    return `${API_URL}/api/cms/pdf-preview-gallery/${activeType}?${params.toString()}`;
  }, [token, activeCompanyId, activeType, refreshKey]);

  // When inputs change, reset the iframe loading state
  useEffect(() => { setIframeLoaded(false); }, [previewUrl]);

  const handleDownload = async () => {
    try {
      const r = await axios.get(previewUrl, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sample_${activeType}_${activeCompany?.trading_name || 'preview'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (_) { /* silent — iframe still shows the error */ }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="pdf-preview-gallery-page">
      {/* Header */}
      <div className="mb-5">
        <BackButton to="/admin/dashboard" label="Dashboard" />
      </div>

      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-[#FF6B35]" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">PDF Preview Gallery</h1>
            <Badge className="bg-[#023E8A] text-white">Admin</Badge>
          </div>
          <p className="text-sm text-[#6C757D] max-w-3xl">
            Live preview of all premium PDF generators with realistic sample data.
            Use this page to validate branding, palette, and layout changes across
            the entire document suite without creating throwaway records.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setRefreshKey((k) => k + 1)}
            data-testid="gallery-refresh-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-1.5" />
            Refresh
          </Button>
          <Button
            onClick={handleDownload}
            className="bg-[#FF6B35] hover:bg-[#E55A28] text-white"
            data-testid="gallery-download-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        {/* LEFT — controls */}
        <div className="space-y-4">
          {/* Sample type picker */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-xs font-bold text-[#6C757D] uppercase tracking-wide mb-3">
                Sample Type
              </h3>
              <div className="space-y-2">
                {SAMPLE_TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = activeType === t.code;
                  return (
                    <button
                      key={t.code}
                      onClick={() => setActiveType(t.code)}
                      data-testid={`gallery-type-${t.code}`}
                      className={`w-full text-left rounded-lg border p-3 transition-all ${
                        active
                          ? 'border-[#023E8A] bg-[#023E8A]/5 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${t.color}15`, color: t.color }}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#2B2D42] truncate">
                            {t.label}
                          </div>
                          <div className="text-[11px] text-[#6C757D] leading-tight line-clamp-2">
                            {t.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Issuer company switcher */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-xs font-bold text-[#6C757D] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                Issuer Company
              </h3>
              {loadingCompanies ? (
                <div className="flex items-center gap-2 text-sm text-[#6C757D]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : companies.length === 0 ? (
                <p className="text-xs text-[#6C757D]">No companies configured.</p>
              ) : (
                <div className="space-y-2">
                  {companies.map((c) => {
                    const active = c.id === activeCompanyId;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setActiveCompanyId(c.id)}
                        data-testid={`gallery-company-${c.id}`}
                        className={`w-full text-left rounded-lg border p-2.5 transition-all ${
                          active
                            ? 'border-[#023E8A] bg-[#023E8A]/5'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: c.theme_color || '#023E8A' }}
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#2B2D42] truncate">
                              {c.trading_name || c.legal_name}
                            </div>
                            <div className="text-[11px] text-[#6C757D] truncate">
                              TRN {c.trn || '—'} · {c.country || '—'}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current selection summary */}
          <Card className="border-dashed border-[#023E8A]/30 bg-[#023E8A]/5">
            <CardContent className="p-4">
              <div className="text-[11px] font-bold text-[#023E8A] uppercase tracking-wide mb-1">
                Current Preview
              </div>
              <div className="text-sm font-semibold text-[#2B2D42]">
                {activeMeta.label}
              </div>
              <div className="text-xs text-[#6C757D] mt-1">
                {activeCompany?.legal_name || 'Default fallback'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — iframe preview */}
        <Card className="overflow-hidden">
          <div
            className="h-2"
            style={{ backgroundColor: activeMeta.color }}
          />
          <div className="relative bg-slate-100" style={{ minHeight: '80vh' }}>
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <div className="flex items-center gap-2 text-sm text-[#6C757D] bg-white/80 backdrop-blur px-4 py-2 rounded-full border border-slate-200">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Rendering {activeMeta.label}…
                </div>
              </div>
            )}
            <iframe
              key={previewUrl}
              src={previewUrl}
              onLoad={() => setIframeLoaded(true)}
              title={`Sample ${activeMeta.label}`}
              className="w-full h-full block border-0"
              style={{ minHeight: '80vh' }}
              data-testid="gallery-preview-iframe"
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PDFPreviewGallery;
